import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldStar, ChartLine, GraduationCap, Certificate, CheckCircle, Books } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const DOMAINS = [
    { title: "Internal Audit Roles and Responsibilities", subtitle: "Section 1 · 20%", body: "Roles, competencies, and coordination — the mandate of internal audit in risk management and how to rely on other assurance providers.", icon: ShieldStar },
    { title: "Risk Management Governance", subtitle: "Section 2 · 25%", body: "Governance frameworks, risk culture, and how risk management integrates with strategy, performance, and emerging risks.", icon: Certificate },
    { title: "Risk Management Assurance", subtitle: "Section 3 · 55%", body: "Risk assessment approaches, data analytics, assurance processes across the SDLC and cyber, reporting, and communication to the board.", icon: ChartLine },
];

export default function Landing() {
    const { user } = useAuth();
    const [preview, setPreview] = useState([]);
    const [plans, setPlans] = useState([]);

    useEffect(() => {
        api.get("/questions/public/preview").then((r) => setPreview(r.data.items || [])).catch(() => {});
        api.get("/payments/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {});
    }, []);

    return (
        <div className="relative">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-border bg-secondary/40">
                <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 py-24 md:grid-cols-12 md:py-32">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="md:col-span-7"
                    >
                        <div className="overline mb-6 text-muted-foreground" data-testid="hero-overline">Certification in Risk Management Assurance — CRMA</div>
                        <h1 className="font-display text-5xl font-semibold tracking-tight text-foreground sm:text-6xl lg:text-7xl">
                            Master the <span className="gold-underline">CRMA</span>. <br />
                            Pass the exam with quiet confidence.
                        </h1>
                        <p className="mt-8 max-w-xl text-lg leading-relaxed text-muted-foreground">
                            A professionally curated question bank aligned to the current IIA CRMA syllabus — Internal Audit Roles (20%), Risk Management Governance (25%), and Risk Management Assurance (55%). Practice with instant feedback, sit timed simulations, and track your mastery across every section.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link to={user ? "/practice" : "/register"} data-testid="hero-cta-primary">
                                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                                    Start practicing <ArrowRight size={18} className="ml-2" />
                                </Button>
                            </Link>
                            <a href="#pricing" data-testid="hero-cta-secondary">
                                <Button size="lg" variant="ghost">View pricing</Button>
                            </a>
                        </div>
                        <div className="mt-10 flex items-center gap-8 text-xs text-muted-foreground">
                            <div className="flex items-center gap-2"><CheckCircle weight="fill" className="text-primary" size={16} /> IIA-aligned domains</div>
                            <div className="flex items-center gap-2"><CheckCircle weight="fill" className="text-primary" size={16} /> Detailed explanations</div>
                            <div className="flex items-center gap-2"><CheckCircle weight="fill" className="text-primary" size={16} /> Exam simulator</div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="relative md:col-span-5"
                    >
                        <div className="relative aspect-[4/5] overflow-hidden rounded-md border border-border shadow-xl">
                            <img
                                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&w=900&q=85"
                                alt="Risk management dashboard on a laptop"
                                className="h-full w-full object-cover"
                            />
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[hsl(30_15%_8%)] via-[hsl(30_15%_8%_/_0.65)] to-transparent p-6 text-white">
                                <div className="overline text-primary">Live risk analytics</div>
                                <div className="mt-2 font-display text-2xl">Practice how you'll actually assess risk.</div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* DOMAINS */}
            <section id="features" className="border-b border-border bg-background py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <div className="mb-16 max-w-2xl">
                        <div className="overline mb-3 text-primary">Coverage</div>
                        <h2 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">Three sections. One curriculum.</h2>
                        <p className="mt-4 text-muted-foreground">Every question is mapped to a section of the current IIA CRMA syllabus so you always know where you stand.</p>
                    </div>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                        {DOMAINS.map((d) => {
                            const Icon = d.icon;
                            return (
                                <motion.div
                                    key={d.title}
                                    whileHover={{ y: -4 }}
                                    transition={{ duration: 0.2 }}
                                    className="group relative border border-border bg-card p-8 transition-shadow hover:shadow-lg"
                                    data-testid={`domain-card-${d.subtitle.toLowerCase().replace(/\s/g, "-")}`}
                                >
                                    <div className="mb-6 flex items-center gap-3">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-sm border border-primary/30 text-primary">
                                            <Icon weight="duotone" size={22} />
                                        </div>
                                        <div className="overline text-muted-foreground">{d.subtitle}</div>
                                    </div>
                                    <h3 className="font-display text-2xl font-semibold">{d.title}</h3>
                                    <p className="mt-3 leading-relaxed text-muted-foreground">{d.body}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SAMPLE */}
            <section className="border-b border-border bg-secondary/30 py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
                        <div className="max-w-2xl">
                            <div className="overline mb-3 text-primary">Sample</div>
                            <h2 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">A taste of the question bank.</h2>
                            <p className="mt-3 text-muted-foreground">Three preview questions. Create an account for full answers, explanations and exam mode.</p>
                        </div>
                        <Link to="/register" data-testid="sample-cta">
                            <Button variant="outline">
                                <Books size={16} className="mr-2" /> Unlock the full bank
                            </Button>
                        </Link>
                    </div>

                    <div className="space-y-6">
                        {preview.map((q, idx) => (
                            <div key={q.id} className="border border-border bg-card p-8" data-testid={`preview-question-${idx}`}>
                                <div className="overline mb-3 text-muted-foreground">{q.domain}</div>
                                <p className="text-lg leading-relaxed">{q.question}</p>
                                <ul className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
                                    {q.options.map((o, i) => (
                                        <li key={i} className="flex items-start gap-3 rounded-sm border border-border bg-background p-3 text-sm">
                                            <span className="font-mono text-xs text-primary">{String.fromCharCode(65 + i)}</span>
                                            <span>{o}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                        {preview.length === 0 && <div className="text-muted-foreground">Loading sample questions…</div>}
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section id="pricing" className="border-b border-border bg-background py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <div className="mb-16 max-w-2xl">
                        <div className="overline mb-3 text-primary">Pricing</div>
                        <h2 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">Invest in your credential.</h2>
                        <p className="mt-3 text-muted-foreground">Choose the plan that fits your study runway.</p>
                    </div>

                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                        {plans.map((p) => (
                            <PricingCard
                                key={p.lookup_key}
                                name={p.name}
                                price={`$${(p.amount / 100).toFixed(0)}`}
                                interval={p.recurring ? `per ${p.recurring}` : "one-time"}
                                desc={p.recurring === "year" ? "Best value — pay for the year, save two months." : "Flexible month-to-month access."}
                                features={["Full question bank (24+ and growing)", "Practice + Exam simulator", "Detailed explanations", "Performance analytics", "Bookmarking"]}
                                cta="Choose plan"
                                to="/pricing"
                                highlighted={p.lookup_key === "yearly"}
                                testid={`pricing-${p.lookup_key}`}
                            />
                        ))}
                    </div>
                </div>
            </section>

            <footer className="border-t border-border py-12">
                <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-6 md:flex-row md:items-center">
                    <div className="flex items-center gap-3">
                        <ShieldStar weight="duotone" className="text-primary" size={20} />
                        <span className="font-display text-lg">CRMA Vault</span>
                    </div>
                    <div className="text-xs text-muted-foreground">© {new Date().getFullYear()} CRMA Vault · Not affiliated with The IIA · Content aligned to publicly available CRMA syllabus.</div>
                </div>
            </footer>
        </div>
    );
}

function PricingCard({ name, price, interval, desc, features, cta, to, highlighted, testid }) {
    const Card = (
        <div className={highlighted ? "tracing-border p-[2px]" : ""}>
            <div className={`h-full ${highlighted ? "rounded-md" : "border border-border"} bg-card p-8`}>
                {highlighted && <div className="overline mb-4 inline-block rounded-full bg-primary/15 px-3 py-1 text-primary">Recommended</div>}
                <h3 className="font-display text-2xl font-semibold">{name}</h3>
                <div className="mt-6 flex items-baseline gap-2">
                    <span className="font-display text-5xl font-semibold tracking-tight">{price}</span>
                    {interval && <span className="text-sm text-muted-foreground">{interval}</span>}
                </div>
                <p className="mt-3 text-sm text-muted-foreground">{desc}</p>
                <ul className="mt-6 space-y-3 text-sm">
                    {features.map((f) => (
                        <li key={f} className="flex items-start gap-2">
                            <CheckCircle weight="fill" size={16} className="mt-0.5 flex-shrink-0 text-primary" />
                            <span>{f}</span>
                        </li>
                    ))}
                </ul>
                <Link to={to} className="mt-8 block" data-testid={`${testid}-cta`}>
                    <Button className={`w-full ${highlighted ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`} variant={highlighted ? "default" : "outline"}>
                        {cta}
                    </Button>
                </Link>
            </div>
        </div>
    );
    return <div data-testid={testid}>{Card}</div>;
}
